import { useContext, useState } from "react";
import { FullDomainNamesComponent } from "./component";
import { BgwContext } from "../../../contexts/backend_gateway/context";
import { AlertsContext } from "../../../contexts/alerts/context";
import { EnvironmentsContext } from "../../../contexts/environments/context";
import {
  useCreateMyHostedZoneWithCertMutation,
  useGetMyCertificateCnameRecordStatusLazyQuery,
  useGetMyHostedZoneChangeStatusLazyQuery,
  useGetMyHostedZonesQuery,
} from "../../../services/backend_gateway/__generated__/backend_gateway-types";
import { AlertType } from "../../../contexts/alerts/type";
import { FeaturesContext } from "../../../contexts/features/context";

export const FullDomainNamesContainer = (props: {
  deploymentId: string;
  navigateToHelp: (stepId: string) => void;
}) => {
  const { deploymentId, navigateToHelp } = props;
  const { bgwService } = useContext(BgwContext);
  const { addAlert } = useContext(AlertsContext);
  const { loading: envLoading, deployments } = useContext(EnvironmentsContext);
  const { customDomainEnabled } = useContext(FeaturesContext);

  const deployment = deployments.find((d) => d.id === deploymentId);

  const {
    data: getMyHostedZonesData,
    loading: getMyHostedZonesLoading,
    refetch: getMyHostedZonesRefetch,
  } = useGetMyHostedZonesQuery({
    client: bgwService.getClient(),
    variables: {
      deploymentId,
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const [createHostedZoneLoading, setCreateHostedZoneLoading] = useState(false);

  const [
    createMyHostedZoneWithCert,
    { loading: createMyHostedZoneWithCertLoading },
  ] = useCreateMyHostedZoneWithCertMutation({
    client: bgwService.getClient(),
    onError: (error) => {
      setCreateHostedZoneLoading(false);
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const [
    getMyHostedZoneChangeStatus,
    { loading: getMyHostedZoneChangeStatusLoading },
  ] = useGetMyHostedZoneChangeStatusLazyQuery({
    client: bgwService.getClient(),
    fetchPolicy: "network-only",
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const [hostedZoneStatuses, setHostedZoneStatuses] = useState<
    Record<string, string | null>
  >({});

  const pollHostedZoneChangeStatus = (
    domainName: string,
    changeId: string,
    callback: () => void
  ) => {
    getMyHostedZoneChangeStatus({
      variables: {
        deploymentId,
        changeInfoId: changeId,
      },
      onCompleted: (data) => {
        const changeStatus = data?.getMyHostedZoneChangeStatus;
        if (changeStatus) {
          setHostedZoneStatuses((prev) => ({
            ...prev,
            [domainName]: changeStatus,
          }));
        }

        // console.log(`Change status for ${domainName}: ${changeStatus}`);
        if (changeStatus !== "INSYNC") {
          setTimeout(
            () => pollHostedZoneChangeStatus(domainName, changeId, callback),
            3000
          );
        } else {
          // console.log(`Hosted zone change for ${domainName} is now INSYNC.`);
          getMyHostedZonesRefetch();
          callback();
        }
      },
      // Optionally, retry on error.
      onError: (error) => {
        setTimeout(
          () => pollHostedZoneChangeStatus(domainName, changeId, callback),
          15000
        );
      },
    });
  };

  const [
    getMyCertificateCnameRecordStatus,
    { loading: getMyCertificateCnameRecordStatusLoading },
  ] = useGetMyCertificateCnameRecordStatusLazyQuery({
    client: bgwService.getClient(),
    fetchPolicy: "network-only",
    onError: (error) => {
      if (error?.message !== undefined) {
        addAlert({
          text: error.message!,
          type: AlertType.WARNING,
        });
      }
    },
  });

  const [certDnsStatuses, setCertDnsStatuses] = useState<
    Record<string, string | null>
  >({});

  const pollCertDnsChangeStatus = (
    domainName: string,
    changeId: string,
    callback: () => void
  ) => {
    getMyCertificateCnameRecordStatus({
      variables: {
        deploymentId,
        changeInfoId: changeId,
      },
      onCompleted: (data) => {
        const changeStatus = data?.getMyCertificateCnameRecordStatus;
        if (changeStatus) {
          setCertDnsStatuses((prev) => ({
            ...prev,
            [domainName]: changeStatus,
          }));
        }

        // console.log(`Change status for ${domainName}: ${changeStatus}`);
        if (changeStatus !== "INSYNC") {
          setTimeout(
            () => pollCertDnsChangeStatus(domainName, changeId, callback),
            3000
          );
        } else {
          // console.log(`Hosted zone change for ${domainName} is now INSYNC.`);
          // getMyCertificateCnameRecordStatusRefetch();
          callback();
        }
      },
      // Optionally, retry on error.
      onError: (error) => {
        setTimeout(
          () => pollCertDnsChangeStatus(domainName, changeId, callback),
          15000
        );
      },
    });
  };

  const createHostedZone = (domainName: string) => {
    if (!customDomainEnabled) return;
    setCreateHostedZoneLoading(true);
    setHostedZoneStatuses((prev) => ({
      ...prev,
      [domainName]: "IN_PROGRESS",
    }));
    createMyHostedZoneWithCert({
      variables: {
        deploymentId,
        fullDomainName: domainName,
      },
      onCompleted: (data) => {
        setCreateHostedZoneLoading(false);
        const result = data?.createMyHostedZoneWithCert || null;
        if (!result) {
          return;
        }
        const { hostedZoneChangeInfoId, certDnsChangeInfoId } = result;
        if (
          hostedZoneChangeInfoId !== null &&
          hostedZoneChangeInfoId !== undefined
        ) {
          pollHostedZoneChangeStatus(domainName, hostedZoneChangeInfoId, () => {
            setHostedZoneStatuses((prev) => ({
              ...prev,
              [domainName]: "COMPLETED",
            }));
          });
        }
        if (certDnsChangeInfoId !== null && certDnsChangeInfoId !== undefined) {
          pollCertDnsChangeStatus(domainName, certDnsChangeInfoId, () => {
            setCertDnsStatuses((prev) => ({
              ...prev,
              [domainName]: "COMPLETED",
            }));
          });
        }
      },
    });
  };

  return (
    <FullDomainNamesComponent
      loading={
        envLoading ||
        getMyHostedZonesLoading ||
        createHostedZoneLoading ||
        createMyHostedZoneWithCertLoading ||
        getMyHostedZoneChangeStatusLoading ||
        getMyCertificateCnameRecordStatusLoading
      }
      deployment={deployment || null}
      customDomainEnabled={customDomainEnabled}
      existingHostedZones={getMyHostedZonesData?.getMyHostedZones || []}
      createHostedZone={createHostedZone}
      hostedZoneStatuses={hostedZoneStatuses}
      certDnsStatuses={certDnsStatuses}
      navigateToHelp={() => navigateToHelp("full_domain_names")}
    />
  );
};
